/**
 * `GET /api/cashflow` si `PUT /api/cashflow/:data/:accountId`.
 *
 * Doua lucruri pe care fisierul le amesteca si care aici raman separate pana in cache:
 *
 *   1. **Soldul in valuta e un fapt; conversia in lei e o decizie.** `balanceMinor` (in moneda
 *      CONTULUI) si `balanceRonMinor` + `rateUsedE6` + `rateSource` sunt campuri diferite. Cand
 *      cursul lipseste, `balanceRonMinor` e `null` si se afiseaza `—`. Hookul nu are voie sa
 *      transforme `null` in 0: un cont in euro cu sold neconvertit ar duce disponibilul zilei la
 *      zero si ar face sa para ca nu sunt bani.
 *   2. **Liniile de credit sunt facilitati, nu deficite** — vin grupate separat, cu `accesatMinor`
 *      calculat din plafon, nu dintr-o formula copiata in 33 de coloane.
 *
 * `positions` e SPARSE: o celula absenta inseamna „negasita in foaie", nu „zero raportat de banca".
 */

import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';

import { api } from '../lib/api.ts';
import { chei } from '../lib/query-keys.ts';
import type { CashflowQuery, UpsertCashPositionInput } from '../../../../functions/api/schemas.ts';

export type IntervalCashflow = CashflowQuery;

export interface ContCashflow {
  id: number;
  bankId: number | null;
  bankCode: string | null;
  label: string;
  currency: string;
  rowKind: string;
  group: 'Conturi' | 'Linii de credit' | 'Non-bancar';
  isCreditLine: boolean;
  creditLimitMinor: number | null;
  creditLimitCurrency: string | null;
  maturityDate: string | null;
  maturityNote: string | null;
  portalUrl: string | null;
  legalEntity: string;
  inAvailableRon: boolean;
  inAvailableFx: boolean;
  inAccountTotal: boolean;
  derivedExpr: string | null;
  excelRow: number | null;
  sortOrder: number;
}

export interface PozitieCash {
  id: number;
  businessDate: string;
  accountId: number;
  currency: string;
  balanceMinor: number | null;
  /** `null` cand nu exista curs. Nu devine 0. */
  balanceRonMinor: number | null;
  rateUsedE6: number | null;
  rateSource: string;
  rateDate: string | null;
  legacyExpression: string | null;
  legacyValueReal: number | null;
  isBlankInSource: boolean;
  excelCell: string | null;
  note: string | null;
  version: number;
}

export interface ZiCashflow {
  businessDate: string;
  isBusinessDay: boolean;
  weekdayRo: string | null;
  availableRonMinor: number | null;
  availableFxMinor: number | null;
  disponibilMinor: number;
  inAccountsMinor: number | null;
  positionCount: number;
  /** Cate poziții nu au cifra in lei fiindca lipseste cursul. */
  missingRateCount: number;
  dePlataMinor: number;
  blocatMinor: number;
  dupaPlataMinor: number;
  entryCount: number;
  runCount: number;
  transferuriMinor: number;
  targetLinieMinor: number | null;
  scadentaRaiffeisenMinor: number | null;
  scadentaRaiffeisenDate: string | null;
  biletLaOrdinMinor: number | null;
  rezultatIncPlatiMinor: number | null;
  totalCurierOnlineOpMinor: number | null;
  excelColumn: string | null;
  note: string | null;
  /** Cifrele Excel, pastrate pentru pagina de reconciliere. Nu se folosesc in niciun calcul. */
  legacy: {
    disponibilLeiReal: number | null;
    disponibilValutaReal: number | null;
    totalTransferuriReal: number | null;
    disponibilInContReal: number | null;
  };
}

export interface LinieCredit {
  businessDate: string;
  accountId: number;
  label: string;
  currency: string;
  maturityDate: string | null;
  creditLimitMinor: number | null;
  balanceMinor: number | null;
  balanceRonMinor: number | null;
  accesatMinor: number | null;
}

export interface TransferCash {
  id: number;
  businessDate: string;
  transferKey: string;
  labelRaw: string;
  accountId: number | null;
  currency: string;
  amountMinor: number | null;
  isBlankInSource: boolean;
  note: string | null;
  version: number;
}

export interface RaspunsCashflow {
  range: { from: string; to: string };
  groups: Record<'Conturi' | 'Linii de credit' | 'Non-bancar', number[]>;
  accounts: ContCashflow[];
  days: ZiCashflow[];
  positions: PozitieCash[];
  creditLines: LinieCredit[];
  transfers: TransferCash[];
}

export function useCashflow(
  interval: IntervalCashflow,
  optiuni?: { activ?: boolean },
): UseQueryResult<RaspunsCashflow, Error> {
  return useQuery({
    queryKey: chei.cashflow.interval(interval),
    queryFn: ({ signal }) =>
      api.get<RaspunsCashflow>('/cashflow', {
        interogare: {
          from: interval.from,
          to: interval.to,
          legalEntity: interval.legalEntity,
        },
        semnal: signal,
      }),
    enabled: optiuni?.activ !== false && interval.from !== '' && interval.to !== '',
  });
}

// ---------------------------------------------------------------------------
// PUT /api/cashflow/:data/:accountId
// ---------------------------------------------------------------------------

export interface RaspunsPozitieCash {
  position: PozitieCash;
  /** Poziția zilei recalculata, ca headerul sa nu ceara inca o data toata pagina de cashflow. */
  day: {
    availableRonMinor: number | null;
    availableFxMinor: number | null;
    disponibilMinor: number;
    dupaPlataMinor: number;
  };
}

export interface VariabilePozitieCash {
  data: string;
  accountId: number;
  pozitie: UpsertCashPositionInput;
}

export function fnPutPozitieCash(variabile: VariabilePozitieCash): Promise<RaspunsPozitieCash> {
  return api.put<RaspunsPozitieCash>(
    `/cashflow/${variabile.data}/${variabile.accountId}`,
    variabile.pozitie,
  );
}

/**
 * Scrierea unui sold.
 *
 * Fara actualizare optimista, deliberat: `balanceRonMinor` depinde de curs, de moneda contului si
 * de politica de conversie, iar serverul e singurul care le stie pe toate trei. O cifra in lei
 * ghicita in client si dezmintita 200 ms mai tarziu ar fi exact felul de „aproape corect" pe care
 * intreaga aplicatie il elimina. `version` merge in corp: rutele PUT au aceeasi garda de
 * concurenta optimista ca restul, deci un 409 cere recitirea poziției.
 */
export function usePutPozitieCash(): UseMutationResult<
  RaspunsPozitieCash,
  Error,
  VariabilePozitieCash
> {
  const queryClient = useQueryClient();
  return useMutation<RaspunsPozitieCash, Error, VariabilePozitieCash>({
    mutationKey: chei.mutatii.pozitieCash(),
    mutationFn: fnPutPozitieCash,
    onSuccess: (_raspuns, variabile) => {
      void queryClient.invalidateQueries({ queryKey: chei.cashflow.tot() });
      // Disponibilul zilei se vede si in ecranul zilei si in scrubberul matricei.
      void queryClient.invalidateQueries({ queryKey: chei.zi.data(variabile.data) });
      void queryClient.invalidateQueries({ queryKey: chei.matrice.tot() });
    },
  });
}

/** Cauta poziția unui cont intr-o zi. Absenta ei inseamna „necompletat", nu „zero". */
export function pozitiaContului(
  raspuns: RaspunsCashflow | undefined,
  businessDate: string,
  accountId: number,
): PozitieCash | undefined {
  return raspuns?.positions.find(
    (p) => p.businessDate === businessDate && p.accountId === accountId,
  );
}
